/**
 * Sortable list wrapper using @dnd-kit. Generic enough for any row component
 * that accepts a {item, dragHandle} render contract.
 *
 * Usage:
 *   <SortableList
 *     items={themes}                        // array of {id, ...}
 *     onReorder={(newItems) => ...}        // newItems is the reordered array
 *     renderRow={(item, dragHandle) => <ThemeRow .../>}
 *   />
 *
 * `dragHandle` is an object containing `{listeners, attributes, setActivatorNodeRef}`
 * that the row component must spread onto its drag handle element.
 */
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableRow({ id, renderRow }) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    listeners,
    attributes,
    setActivatorNodeRef,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 10 : "auto",
  };

  const dragHandle = { listeners, attributes, setActivatorNodeRef };

  return (
    <div ref={setNodeRef} style={style}>
      {renderRow(dragHandle)}
    </div>
  );
}

export default function SortableList({ items, onReorder, renderRow }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((it) => it.id === active.id);
    const newIndex = items.findIndex((it) => it.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    onReorder?.(next);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((it) => it.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {items.map((item) => (
            <SortableRow
              key={item.id}
              id={item.id}
              renderRow={(dragHandle) => renderRow(item, dragHandle)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
